import { COMPANY_INFO } from '../data/company';
import {
  LegalNav,
  LegalPageBody,
  LegalPageHero,
  LegalPanel,
  LegalSection,
  ReviewNotice,
  lastUpdated,
} from '../components/common/LegalPageShell';
import Seo from '../components/common/Seo';
import { routeMeta } from '../routeMeta';

const privacySections = [
  { id: 'who-we-are', label: 'Who we are' },
  { id: 'what-we-collect', label: 'What we collect' },
  { id: 'why-we-use-it', label: 'Why we use it' },
  { id: 'where-it-goes', label: 'Where it goes' },
  { id: 'analytics', label: 'Analytics' },
  { id: 'retention', label: 'Retention' },
  { id: 'your-rights', label: 'Your rights' },
  { id: 'international-visitors', label: 'International visitors' },
  { id: 'contact', label: 'Contact' },
];

const PrivacyPolicyPage = () => {
  const primaryEmail = COMPANY_INFO.contact.email?.[0] || 'info@leiongroup.com';

  return (
    <div data-testid="privacy-policy-page">
      <Seo {...routeMeta['/privacy-policy']} path="/privacy-policy" />
      <LegalPageHero
        title="Privacy Policy"
        currentPage="Privacy Policy"
        description="How LEION Engineering handles enquiry information, quote request details, and consent-based website analytics."
        meta={[
          ['Updated', lastUpdated],
          ['Controller', COMPANY_INFO.name],
        ]}
      />

      <LegalPageBody aside={<LegalNav sections={privacySections} />}>
        <ReviewNotice />
        <LegalPanel>
          <LegalSection id="who-we-are" title="Who We Are">
            <p>
              The data controller for this website is {COMPANY_INFO.name}, {COMPANY_INFO.address.office}, {COMPANY_INFO.address.city} - {COMPANY_INFO.address.pincode}, {COMPANY_INFO.address.state}, {COMPANY_INFO.address.country}.
            </p>
            <p>
              Privacy enquiries and data requests can be sent to <a href={`mailto:${primaryEmail}`} className="font-semibold text-[#93C5FD] hover:text-white">{primaryEmail}</a>.
            </p>
          </LegalSection>

          <LegalSection id="what-we-collect" title="What We Collect">
            <p>
              When you submit an enquiry or quote request, the site collects the details entered in the form: full name, company name, email address, phone number, industry, product interest, required capacity, and additional requirements.
            </p>
            <p>
              The compact quote form may collect a smaller visible set: name, company, email, phone, and requirements. A product interest may also be preselected when the form appears on a product page.
            </p>
          </LegalSection>

          <LegalSection id="why-we-use-it" title="Why We Use It">
            <p>
              LEION Engineering uses enquiry details to respond to product, service, support, and quotation enquiries started by the visitor.
            </p>
            <p>
              The lawful basis is legitimate interest in responding to a business enquiry the visitor initiated, and consent where the visitor voluntarily supplies information through the form.
            </p>
          </LegalSection>

          <LegalSection id="where-it-goes" title="Where It Goes">
            <p>
              The website posts quote requests to the LEION Engineering backend endpoint at <span className="font-['JetBrains_Mono'] text-sm text-[#BFDBFE]">/api/quote-request</span>.
            </p>
            <p>
              The backend attempts to store quote requests in the configured MongoDB database and, when SMTP email is configured, sends the enquiry to the configured enquiry inbox. The code defaults that inbox to <a href={`mailto:${primaryEmail}`} className="font-semibold text-[#93C5FD] hover:text-white">{primaryEmail}</a>, but production configuration may override it.
            </p>
            <p>
              If the quote server cannot be reached from the browser, the site opens a direct email addressed to <a href={`mailto:${primaryEmail}`} className="font-semibold text-[#93C5FD] hover:text-white">{primaryEmail}</a>.
            </p>
          </LegalSection>

          <LegalSection id="analytics" title="Analytics">
            <p>
              The repository reviewed for this draft does not load Google Analytics or Google Tag Manager.
            </p>
            <p>
              The site includes first-party analytics that can record page views, button clicks, quote form activity, contact link clicks, page path, referrer, device category, visitor identifier, session identifier, hashed IP address, user agent, and limited event metadata. Analytics is now inactive unless the visitor accepts analytics storage in the cookie banner.
            </p>
            <p>
              If Google Analytics is added later, LEION Engineering should confirm the measurement ID and update this policy before publishing it as final.
            </p>
          </LegalSection>

          <LegalSection id="retention" title="Retention">
            <p>
              Enquiry and analytics records are kept for <span className="font-semibold text-white">[RETENTION PERIOD - CLIENT TO CONFIRM]</span>.
            </p>
          </LegalSection>

          <LegalSection id="your-rights" title="Your Rights">
            <p>
              Visitors can ask to access, correct, or delete their personal data. Visitors can also withdraw consent where processing is based on consent.
            </p>
            <p>
              To exercise these rights, email <a href={`mailto:${primaryEmail}`} className="font-semibold text-[#93C5FD] hover:text-white">{primaryEmail}</a>.
            </p>
          </LegalSection>

          <LegalSection id="international-visitors" title="International Visitors">
            <p>
              The website serves visitors from India, Ghana, Africa, Dubai, and other international markets. Visitors in the EU or UK may have rights under the GDPR or UK GDPR. Visitors in India may have rights under the Digital Personal Data Protection Act, 2023.
            </p>
          </LegalSection>

          <LegalSection id="contact" title="Contact">
            <p>
              For privacy questions, corrections, or data requests, contact LEION Engineering at <a href={`mailto:${primaryEmail}`} className="font-semibold text-[#93C5FD] hover:text-white">{primaryEmail}</a>.
            </p>
          </LegalSection>
        </LegalPanel>
      </LegalPageBody>
    </div>
  );
};

export default PrivacyPolicyPage;
